import React from "react";
import SectionTitle from "../common/SectionTitle";
import SubHeading from "../common/SubHeading";
import { useFormSection, FormSectionProps } from "../../hooks/useFormSection";
import type { SupplementEntry, PrescriptionEntry } from "../../lib/types/IntakeFormTypes";

const yesNoOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const MedicationProfileSectionV2: React.FC<FormSectionProps> = (props) => {
  const { Field, formData, isEditMode = true } = useFormSection(props);
  
  const medicationData = formData?.supplementsMedications || {};

  const handleSupplementChange = (
    supplements: SupplementEntry[],
    index: number,
    field: keyof SupplementEntry,
    value: string
  ) => {
    const newSupps = [...supplements];
    newSupps[index] = { ...newSupps[index], [field]: value };
    return newSupps;
  };

  const addSupplement = (supplements: SupplementEntry[]) => {
    return [
      ...supplements,
      { typeBrand: "", dosage: "", frequency: "", reason: "", startDate: "" },
    ];
  };

  const removeSupplement = (supplements: SupplementEntry[], index: number) => {
    return supplements.filter((_, i) => i !== index);
  };

  const handlePrescriptionChange = (
    prescriptions: PrescriptionEntry[],
    index: number,
    field: keyof PrescriptionEntry,
    value: string
  ) => {
    const newPrescs = [...prescriptions];
    newPrescs[index] = { ...newPrescs[index], [field]: value };
    return newPrescs;
  };

  const addPrescription = (prescriptions: PrescriptionEntry[]) => {
    return [
      ...prescriptions,
      {
        medication: "",
        dosage: "",
        frequency: "",
        reason: "",
        startEndDate: "",
      },
    ];
  };

  const removePrescription = (prescriptions: PrescriptionEntry[], index: number) => {
    return prescriptions.filter((_, i) => i !== index);
  };

  return (
    <section>
      <SectionTitle>Supplements & Medications</SectionTitle>

      <SubHeading>Supplements</SubHeading>
      <p className="mb-2 text-brand-stone-600">
        List vitamins/supplements/enhancers/herbs/powders are you currently
        taking, including brand name if possible:
      </p>
      
      <Field
        path="supplementsMedications.supplements"
        render={({ value = [], onChange }) => (
          <>
            {value.map((s: SupplementEntry, i: number) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-6 gap-2 p-3 mb-2 border rounded-lg bg-brand-stone-50 items-end"
              >
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Type & Brand</label>
                  <input
                    type="text"
                    value={s.typeBrand}
                    onChange={(e) =>
                      onChange(handleSupplementChange(value, i, "typeBrand", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Dosage</label>
                  <input
                    type="text"
                    value={s.dosage}
                    onChange={(e) =>
                      onChange(handleSupplementChange(value, i, "dosage", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Frequency</label>
                  <input
                    type="text"
                    value={s.frequency}
                    onChange={(e) =>
                      onChange(handleSupplementChange(value, i, "frequency", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Reason for Use</label>
                  <input
                    type="text"
                    value={s.reason}
                    onChange={(e) =>
                      onChange(handleSupplementChange(value, i, "reason", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div>
                    <label className="text-sm font-medium">Start Date</label>
                    <input
                      type="date"
                      value={s.startDate}
                      onChange={(e) =>
                        onChange(handleSupplementChange(value, i, "startDate", e.target.value))
                      }
                      className="w-full mt-1 p-2 border rounded bg-white"
                      disabled={!isEditMode}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onChange(removeSupplement(value, i))}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 h-10"
                    aria-label={`Remove supplement ${i + 1}`}
                    disabled={!isEditMode}
                  >
                    &times;
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onChange(addSupplement(value))}
              className="bg-brand-green-600 text-white px-4 py-2 rounded hover:bg-brand-green-700"
              disabled={!isEditMode}
            >
              + Add Supplement
            </button>
          </>
        )}
      />

      <SubHeading>Prescription Medications</SubHeading>
      <p className="mb-2 text-brand-stone-600">
        List prescription medications you are currently taking or have taken in
        the last 5 years:
      </p>
      
      <Field
        path="supplementsMedications.prescriptions"
        render={({ value = [], onChange }) => (
          <>
            {value.map((p: PrescriptionEntry, i: number) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-6 gap-2 p-3 mb-2 border rounded-lg bg-brand-stone-50 items-end"
              >
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Medication</label>
                  <input
                    type="text"
                    value={p.medication}
                    onChange={(e) =>
                      onChange(handlePrescriptionChange(value, i, "medication", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Dosage</label>
                  <input
                    type="text"
                    value={p.dosage}
                    onChange={(e) =>
                      onChange(handlePrescriptionChange(value, i, "dosage", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Frequency</label>
                  <input
                    type="text"
                    value={p.frequency}
                    onChange={(e) =>
                      onChange(handlePrescriptionChange(value, i, "frequency", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Reason for Use</label>
                  <input
                    type="text"
                    value={p.reason}
                    onChange={(e) =>
                      onChange(handlePrescriptionChange(value, i, "reason", e.target.value))
                    }
                    className="w-full mt-1 p-2 border rounded bg-white"
                    disabled={!isEditMode}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div>
                    <label className="text-sm font-medium">Start/End Date</label>
                    <input
                      type="text"
                      value={p.startEndDate}
                      onChange={(e) =>
                        onChange(handlePrescriptionChange(value, i, "startEndDate", e.target.value))
                      }
                      className="w-full mt-1 p-2 border rounded bg-white"
                      disabled={!isEditMode}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onChange(removePrescription(value, i))}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 h-10"
                    aria-label={`Remove prescription ${i + 1}`}
                    disabled={!isEditMode}
                  >
                    &times;
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onChange(addPrescription(value))}
              className="bg-brand-green-600 text-white px-4 py-2 rounded hover:bg-brand-green-700"
              disabled={!isEditMode}
            >
              + Add Prescription
            </button>
          </>
        )}
      />

      <SubHeading>Medication History & Side Effects</SubHeading>
      
      <Field
        path="supplementsMedications.sideEffects"
        label="Do your medications or supplements ever cause unusual side effects or problems?"
        type="radio"
        options={yesNoOptions}
      />
      
      {medicationData.sideEffects === "yes" && (
        <Field
          path="supplementsMedications.sideEffectsDetails"
          label="If yes, indicate which supplement or medication and describe side effects:"
          type="textarea"
          rows={3}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-4">
        <Field
          path="supplementsMedications.nsaidsUse"
          label="Have you had prolonged or regular use of NSAIDs (Advil, Aleve)?"
          type="radio"
          options={yesNoOptions}
        />
        
        <Field
          path="supplementsMedications.tylenolUse"
          label="Have you had prolonged or regular use of Tylenol?"
          type="radio"
          options={yesNoOptions}
        />
        
        <Field
          path="supplementsMedications.acidBlockersUse"
          label="Have you had prolonged or regular use of Acid Blocking Drugs (Zantac, Prilosec, etc.)?"
          type="radio"
          options={yesNoOptions}
        />
        
        <Field
          path="supplementsMedications.antibiotics3xYear"
          label="Antibiotics use more than 3 times/year?"
          type="radio"
          options={yesNoOptions}
        />
        
        <Field
          path="supplementsMedications.longTermAntibiotics"
          label="Long term antibiotics at any time during your life?"
          type="radio"
          options={yesNoOptions}
        />
        
        <Field
          path="supplementsMedications.steroidsUse"
          label="Use of steroids (prednisone, nasal allergy inhalers) in the past?"
          type="radio"
          options={yesNoOptions}
        />
      </div>

      <Field
        path="supplementsMedications.otherMedication"
        label="Prolonged use of any medication not listed? (SSRIs, Anti-Anxieties, Sleeping pills etc)"
        type="radio"
        options={yesNoOptions}
      />
      
      {medicationData.otherMedication === "yes" && (
        <Field
          path="supplementsMedications.otherMedicationDetails"
          label="If yes please list which and duration"
          type="textarea"
          rows={3}
        />
      )}

      <Field
        path="supplementsMedications.recreationalDrugs"
        label="Do you use recreational drugs?"
        type="radio"
        options={yesNoOptions}
      />
      
      {medicationData.recreationalDrugs === "yes" && (
        <Field
          path="supplementsMedications.recreationalDrugsDetails"
          label="If yes, how often and what type (all answers are confidential)"
          type="textarea"
          rows={3}
        />
      )}

      <Field
        path="supplementsMedications.marijuanaCBD"
        label="Do you use Marijuana or CBD therapeutically?"
        type="radio"
        options={yesNoOptions}
      />
      
      {medicationData.marijuanaCBD === "yes" && (
        <Field
          path="supplementsMedications.marijuanaCBDDetails"
          label="If yes, what forms and how often (edibles, oils, etc.): If you use CBD please indicate the dosage, frequency, and what you use it for:"
          type="textarea"
          rows={3}
        />
      )}

      <Field
        path="supplementsMedications.smoke"
        label="Do you smoke?"
        type="radio"
        options={yesNoOptions}
      />
      
      {medicationData.smoke === "yes" && (
        <Field
          path="supplementsMedications.smokeDetails"
          label="How many years? Packs per day? Previous smoking: How many years? Packs per day: Second hand smoke exposure?"
          type="textarea"
          rows={3}
        />
      )}
    </section>
  );
};

export default MedicationProfileSectionV2;